import { Box, CssBaseline, type CSSObject, List, styled, type Theme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

import { SidebarItem, type SidebarItemDetail } from './components/SidebarItem';

export type SidebarProps = {
  /**
   * @props {string} id - The id of the sidebar.
   * @default 'sidebar'
   */
  id?: string;

  /**
   * @props {string} selectedItem - The selected item in the sidebar.
   * @default ''
   */
  selectedItem?: string;

  /**
   * @props {SidebarItem[]} items - The items in the sidebar.
   * @default []
   */
  items?: SidebarItemDetail[];

  /**
   * @props {boolean} isSidebarOpen - The state of the sidebar.
   * @default false
   */
  isSidebarOpen?: boolean;
};

// This is the width of the sidebar when closed and open using theme spacing
const sidebarSpacingClosed = '64px'; // width of the sidebar when closed - not using theme spacing - width of component
const sidebarSpacingOpen = '320px'; // width of the sidebar when open - not using theme spacing - width of component and value not in spacing values

const openedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  // overflowX: 'hidden',
  position: 'relative',
  width: sidebarSpacingOpen,
  backgroundColor: theme.palette.grey[100],
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeIn,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // overflowX: 'hidden',
  width: sidebarSpacingClosed,
  position: 'relative',
  backgroundColor: theme.palette.grey[100],
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

export function Sidebar(props: SidebarProps) {
  const { id = 'sidebar', selectedItem, items, isSidebarOpen } = props;

  return (
    <Box data-testid={`${id}`} sx={{ width: sidebarSpacingClosed, height: '100%' }}>
      <CssBaseline />
      <Drawer
        data-testid={`${id}-drawer`}
        variant="permanent"
        open={isSidebarOpen}
        sx={{
          flexShrink: 0,
          whiteSpace: 'nowrap',
          height: '100%',
          overflowX: 'hidden',
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <List
          data-testid={`${id}-list`}
          role="list"
          sx={{
            height: 1,
            py: 0, // top / bottom padding
            // selected and (selected + hover) states
            '&& .Mui-selected, && .Mui-selected:hover': (theme) => ({
              backgroundColor: theme.palette.action.selected,
            }),
            // hover states
            '& .MuiListItemButton-root:hover': (theme) => ({
              backgroundColor: theme.palette.grey[300],
            }),
          }}
        >
          {items?.map((item) => (
            <SidebarItem
              data-testid={`${id}-sidebarItem-${item.id}`}
              key={item.id}
              isSidebarOpen={isSidebarOpen}
              {...item}
              selected={selectedItem ? item.id === selectedItem : item.selected}
            />
          ))}
        </List>
      </Drawer>
    </Box>
  );
}

export default Sidebar;
